import React from "react";
import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  isValidSummary,
  parseHtmlToText,
} from "../Services/ReactPDFService.jsx";
import { convertToBulletPoints } from "../Utils/Resume/TextFormattingUtils.js";

// ATS Prime Template - Blue accent, double-line dividers, 4-column skills grid
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: "#ffffff",
    paddingHorizontal: 30,
    paddingTop: 25,
    paddingBottom: 30,
    fontFamily: "Roboto",
  },
  // Header
  headerContainer: {
    marginBottom: 4,
  },
  name: {
    fontSize: 26,
    fontWeight: 700,
    color: "#2563eb",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginBottom: 2,
  },
  professionalTitle: {
    fontSize: 12,
    fontWeight: 700,
    paddingTop:12,
    color: "#1a1a1a",
    textTransform: "uppercase",
    marginBottom: 3,
  },
  contactRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  contactText: {
    fontSize: 9.5,
    color: "#333333",
  },
  contactSeparator: {
    fontSize: 9.5,
    color: "#999999",
    marginHorizontal: 5,
  },
  contactLink: {
    fontSize: 9.5,
    color: "#2563eb",
    textDecoration: "underline",
  },
  // Profile photo
  profilePhotoContainer: {
    width: 70,
    height: 70,
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
  },
  profilePhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  // Section header - double line divider
  sectionHeaderContainer: {
    marginTop: 10,
    marginBottom: 4,
  },
  sectionDividerTop: {
    borderBottomWidth: 1,
    borderBottomColor: "#2563eb",
    marginBottom: 5,
  },
  sectionDividerBottom: {
    borderBottomWidth: 1,
    borderBottomColor: "#2563eb",
    marginBottom: 1,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#2563eb",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 3,
  },
  // Summary
  summaryText: {
    fontSize: 9.5,
    lineHeight: 1.5,
    color: "#333333",
  },
  // Experience
  experienceItem: {
    marginBottom: 8,
  },
  experienceHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 1,
  },
  jobTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  dateText: {
    fontSize: 9.5,
    fontWeight: 700,
    color: "#1a1a1a",
    textAlign: "right",
  },
  companyLocation: {
    fontSize: 9.5,
    color: "#555555",
    marginBottom: 3,
  },
  bulletPoint: {
    fontSize: 9.5,
    marginBottom: 2,
    paddingLeft: 10,
    lineHeight: 1.4,
    color: "#333333",
  },
  // Skills - 4 column grid
  skillsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  skillItem: {
    width: "25%",
    marginBottom: 3,
    paddingRight: 6,
  },
  skillText: {
    fontSize: 9.5,
    color: "#333333",
  },
  // Education
  educationItem: {
    marginBottom: 8,
  },
  educationHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 2,
  },
  degree: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  school: {
    fontSize: 9.5,
    color: "#555555",
    marginTop: 1,
  },
  educationDate: {
    fontSize: 9.5,
    fontWeight: 700,
    color: "#1a1a1a",
    textAlign: "right",
  },
  gpa: {
    fontSize: 9,
    color: "#666666",
    marginTop: 2,
  },
  // Projects
  projectItem: {
    marginBottom: 8,
  },
  projectTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  projectTech: {
    fontSize: 9,
    color: "#666666",
    fontStyle: "italic",
    marginTop: 1,
  },
  // Certifications
  certItem: {
    flexDirection: "row",
    marginBottom: 3,
  },
  certBullet: {
    fontSize: 9.5,
    color: "#333333",
    marginRight: 6,
  },
  certText: {
    fontSize: 9.5,
    color: "#333333",
    flex: 1,
    lineHeight: 1.4,
  },
  // Languages
  languageItem: {
    flexDirection: "row",
    marginBottom: 3,
  },
  languageName: {
    fontSize: 9.5,
    color: "#333333",
    marginRight: 8,
  },
  languageLevel: {
    fontSize: 9.5,
    color: "#666666",
  },
});

// Helper functions
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

// Template-specific bullet renderer
const renderTemplateBullets = (text) => {
  if (!text) return null;
  let textString = "";
  try {
    textString = typeof text === "string" ? text : String(text || "");
  } catch {
    return null;
  }
  const points = convertToBulletPoints(textString);
  return points.map((point, index) => (
    <View key={index} style={{ flexDirection: "row", marginBottom: 2, paddingLeft: 8 }}>
      <Text style={{ fontSize: 9.5, color: "#333333", marginRight: 5, lineHeight: 1.4 }}>{"\u2022"}</Text>
      <Text style={{ fontSize: 9.5, color: "#333333", lineHeight: 1.4, flex: 1 }}>{point}</Text>
    </View>
  ));
};

const ATSPrimeTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
    customSections = [],
    sectionTitles = {},
    summary = "",
    professionalTitle = "",
    profilePhoto = {},
  } = resumeData || {};

  // Get profile photo URL
  const profilePhotoUrl = profilePhoto?.url || personalInfo?.profilePhotoUrl || null;

  // Dynamic styles
  const createDynamicStyles = (baseStyles, customizations) => {
    const hasCustomizations = customizations && Object.keys(customizations).length > 0;
    if (!hasCustomizations) return baseStyles;

    const { fontSize, sectionSpacing, titleColor, subtitleColor, fontFamily } = customizations;
    const customStyleOverrides = {};

    if (fontFamily) {
      customStyleOverrides.page = { ...baseStyles.page, fontFamily };
    }

    if (fontSize) {
      customStyleOverrides.name = { ...baseStyles.name, fontSize: fontSize * 2 };
      customStyleOverrides.professionalTitle = { ...baseStyles.professionalTitle, fontSize: fontSize * 1 };
      customStyleOverrides.sectionTitle = { ...baseStyles.sectionTitle, fontSize: fontSize * 0.85 };
      customStyleOverrides.jobTitle = { ...baseStyles.jobTitle, fontSize: fontSize * 0.8 };
      customStyleOverrides.degree = { ...baseStyles.degree, fontSize: fontSize * 0.8 };
      customStyleOverrides.summaryText = { ...baseStyles.summaryText, fontSize: fontSize * 0.72 };
      customStyleOverrides.bulletPoint = { ...baseStyles.bulletPoint, fontSize: fontSize * 0.72 };
      customStyleOverrides.skillText = { ...baseStyles.skillText, fontSize: fontSize * 0.72 };
    }

    if (sectionSpacing) {
      customStyleOverrides.sectionHeaderContainer = { ...baseStyles.sectionHeaderContainer, marginTop: sectionSpacing };
    }

    if (titleColor) {
      customStyleOverrides.name = {
        ...(customStyleOverrides.name || baseStyles.name),
        color: titleColor,
      };
      customStyleOverrides.sectionTitle = {
        ...(customStyleOverrides.sectionTitle || baseStyles.sectionTitle),
        color: titleColor,
      };
      customStyleOverrides.sectionDividerTop = {
        ...baseStyles.sectionDividerTop,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.sectionDividerBottom = {
        ...baseStyles.sectionDividerBottom,
        borderBottomColor: titleColor,
      };
      customStyleOverrides.contactLink = {
        ...baseStyles.contactLink,
        color: titleColor,
      };
    }

    if (subtitleColor) {
      customStyleOverrides.jobTitle = {
        ...(customStyleOverrides.jobTitle || baseStyles.jobTitle),
        color: subtitleColor,
      };
      customStyleOverrides.degree = {
        ...(customStyleOverrides.degree || baseStyles.degree),
        color: subtitleColor,
      };
      customStyleOverrides.projectTitle = {
        ...baseStyles.projectTitle,
        color: subtitleColor,
      };
    }

    return StyleSheet.create({ ...baseStyles, ...customStyleOverrides });
  };

  const mergedStyles = createDynamicStyles(styles, customStyles);

  // Section titles
  const defaultTitles = {
    summary: "Profile",
    workExperience: "Professional Experience",
    education: "Education",
    skills: "Skills",
    projects: "Projects",
    certifications: "Certifications",
    achievements: "Achievements",
    languages: "Languages",
    awards: "Awards",
    volunteering: "Volunteering",
    interests: "Interests",
    industryExpertise: "Industry Expertise",
    publications: "Publications",
    references: "References",
  };
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Combine all skills
  const allSkills = (() => {
    if (Array.isArray(skills)) return skills;
    if (skills.list && Array.isArray(skills.list) && skills.list.length > 0) return skills.list;
    const combined = [
      ...(skills.technical || []),
      ...(skills.soft || []),
      ...(skills.tools || []),
      ...(skills.other || []),
    ];
    if (combined.length > 0) return combined;
    if (skills.categorized) {
      return [
        ...(skills.categorized.technical || []),
        ...(skills.categorized.soft || []),
        ...(skills.categorized.tools || []),
        ...(skills.categorized.other || []),
      ];
    }
    return [];
  })();

  const uniqueSkills = (() => {
    const seen = new Set();
    return allSkills.filter((skill) => {
      const name = getSkillName(skill).toLowerCase().trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  })();

  // Double-line section header
  const renderSectionHeader = (title) => (
    <View style={mergedStyles.sectionHeaderContainer}>
      <View style={mergedStyles.sectionDividerTop} />
      <Text style={mergedStyles.sectionTitle}>{title}</Text>
      <View style={mergedStyles.sectionDividerBottom} />
    </View>
  );

  // Section order
  const defaultSectionOrder = [
    "summary", "workExperience", "skills", "projects", "education",
    "certifications", "achievements", "languages", "awards",
    "volunteering", "interests", "industryExpertise", "publications", "references",
  ];

  const customSectionIds = customSections
    .filter((cs) => cs.id && cs.title && cs.items?.length > 0)
    .map((cs) => cs.id);

  const baseSectionOrder = customSectionOrder || defaultSectionOrder;
  const finalSectionOrder = [
    ...baseSectionOrder,
    ...customSectionIds.filter((id) => !baseSectionOrder.includes(id)),
  ];

  // Section components
  const sectionComponents = {
    summary: () =>
      summary && isValidSummary(summary) && (
        <View>
          {renderSectionHeader(getSectionTitle("summary"))}
          <Text style={mergedStyles.summaryText}>{parseHtmlToText(summary)}</Text>
        </View>
      ),

    workExperience: () =>
      workExperience.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("workExperience"))}
          {workExperience.map((job, index) => (
            <View key={index} style={mergedStyles.experienceItem} wrap={false}>
              <View style={mergedStyles.experienceHeader}>
                <Text style={mergedStyles.jobTitle}>{job.jobTitle}, {job.company}</Text>
                <Text style={mergedStyles.dateText}>
                  {formatDate(job.startDate)} to{" "}
                  {job.isCurrentJob || job.endDate === "Present" || !job.endDate
                    ? "Present"
                    : formatDate(job.endDate)}
                </Text>
              </View>
              {job.location && (
                <Text style={mergedStyles.companyLocation}>{job.location}</Text>
              )}
              {job.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(job.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    skills: () =>
      uniqueSkills.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("skills"))}
          <View style={mergedStyles.skillsGrid}>
            {uniqueSkills.map((skill, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillText}>{getSkillName(skill)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    education: () =>
      education.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("education"))}
          {education.map((edu, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>{edu.school}</Text>
                  <Text style={mergedStyles.school}>
                    {edu.degree}{edu.fieldOfStudy ? `, ${edu.fieldOfStudy}` : ""}
                    {edu.location ? `, ${edu.location}` : ""}
                  </Text>
                </View>
                <View style={{ alignItems: "flex-end" }}>
                  <Text style={mergedStyles.educationDate}>
                    {formatDate(edu.startDate)} to {edu.endDate === "Present" ? "Present" : formatDate(edu.endDate)}
                  </Text>
                  {edu.gpa && <Text style={mergedStyles.gpa}>GPA: {edu.gpa}</Text>}
                </View>
              </View>
            </View>
          ))}
        </View>
      ),

    projects: () =>
      projects.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("projects"))}
          {projects.map((project, index) => (
            <View key={index} style={mergedStyles.projectItem} wrap={false}>
              <Text style={mergedStyles.projectTitle}>{project.name}</Text>
              {project.technologies && (
                <Text style={mergedStyles.projectTech}>Technologies: {project.technologies}</Text>
              )}
              {project.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(project.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    certifications: () =>
      certifications.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("certifications"))}
          {certifications.map((cert, index) => (
            <View key={index} style={mergedStyles.certItem} wrap={false}>
              <Text style={mergedStyles.certBullet}>{"\u2022"}</Text>
              <Text style={mergedStyles.certText}>
                {cert.name}
                {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
                {cert.date && ` (${formatDate(cert.date)})`}
              </Text>
            </View>
          ))}
        </View>
      ),

    achievements: () =>
      achievements.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("achievements"))}
          {achievements.map((achievement, index) => (
            <View key={index} style={{ marginBottom: 3 }}>
              {achievement.description && (
                <View>{renderTemplateBullets(parseHtmlToText(achievement.description))}</View>
              )}
            </View>
          ))}
        </View>
      ),

    languages: () =>
      languages.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("languages"))}
          <View style={mergedStyles.skillsGrid}>
            {languages.map((lang, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillText}>
                  {getLanguageName(lang)}{lang.proficiency ? ` (${lang.proficiency})` : ""}
                </Text>
              </View>
            ))}
          </View>
        </View>
      ),

    awards: () =>
      awards.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("awards"))}
          {awards.map((award, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <View style={mergedStyles.educationHeader}>
                <View style={{ flex: 1 }}>
                  <Text style={mergedStyles.degree}>{award.title || award.name}</Text>
                  {award.issuer && <Text style={mergedStyles.school}>{award.issuer}</Text>}
                </View>
                {award.date && (
                  <Text style={mergedStyles.educationDate}>{formatDate(award.date)}</Text>
                )}
              </View>
              {award.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(award.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    volunteering: () =>
      volunteering.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("volunteering"))}
          {volunteering.map((vol, index) => (
            <View key={index} style={mergedStyles.experienceItem} wrap={false}>
              <View style={mergedStyles.experienceHeader}>
                <Text style={mergedStyles.jobTitle}>{vol.role}, {vol.organization}</Text>
                <Text style={mergedStyles.dateText}>
                  {formatDate(vol.startDate)} to {vol.isCurrentRole ? "Present" : formatDate(vol.endDate)}
                </Text>
              </View>
              {vol.description && (
                <View style={{ marginTop: 3 }}>
                  {renderTemplateBullets(parseHtmlToText(vol.description))}
                </View>
              )}
            </View>
          ))}
        </View>
      ),

    interests: () =>
      interests.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("interests"))}
          <View style={mergedStyles.skillsGrid}>
            {interests.map((interest, index) => (
              <View key={index} style={mergedStyles.skillItem}>
                <Text style={mergedStyles.skillText}>{getInterestName(interest)}</Text>
              </View>
            ))}
          </View>
        </View>
      ),

    industryExpertise: () =>
      industryExpertise.length > 0 && (
        <View wrap={false}>
          {renderSectionHeader(getSectionTitle("industryExpertise"))}
          {industryExpertise.map((exp, index) => (
            <View key={index} style={mergedStyles.certItem}>
              <Text style={mergedStyles.certBullet}>{"\u2022"}</Text>
              <Text style={mergedStyles.certText}>
                {exp.name}{exp.proficiency ? ` - ${exp.proficiency}` : ""}
                {exp.yearsOfExperience ? ` (${exp.yearsOfExperience})` : ""}
              </Text>
            </View>
          ))}
        </View>
      ),

    publications: () =>
      publications.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("publications"))}
          {publications.map((pub, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <Text style={mergedStyles.degree}>{pub.title}</Text>
              {pub.authors && (
                <Text style={mergedStyles.school}>
                  {Array.isArray(pub.authors) ? pub.authors.join(", ") : pub.authors}
                </Text>
              )}
              <Text style={mergedStyles.educationDate}>
                {pub.journal}{pub.date ? ` \u2022 ${formatDate(pub.date)}` : ""}
              </Text>
            </View>
          ))}
        </View>
      ),

    references: () =>
      references.length > 0 && (
        <View>
          {renderSectionHeader(getSectionTitle("references"))}
          {references.map((ref, index) => (
            <View key={index} style={mergedStyles.educationItem} wrap={false}>
              <Text style={mergedStyles.degree}>{ref.name}</Text>
              <Text style={mergedStyles.school}>
                {ref.title}{ref.company ? ` at ${ref.company}` : ""}
              </Text>
              <Text style={mergedStyles.educationDate}>
                {[ref.email, ref.phone].filter(Boolean).join(" \u2022 ")}
              </Text>
            </View>
          ))}
        </View>
      ),

    // Custom sections
    ...customSections.reduce((acc, customSection) => {
      if (customSection.id && customSection.title && customSection.items?.length > 0) {
        acc[customSection.id] = () => (
          <View wrap={false}>
            {renderSectionHeader(customSection.title)}
            {customSection.items.map((item, itemIndex) => (
              <View key={itemIndex} style={{ marginBottom: 3 }}>
                {item.content && (
                  <Text style={mergedStyles.summaryText}>{parseHtmlToText(item.content)}</Text>
                )}
              </View>
            ))}
          </View>
        );
      }
      return acc;
    }, {}),
  };

  return (
    <Document>
      <Page size="A4" style={mergedStyles.page} wrap={true}>
        {/* Header */}
        <View style={{ ...mergedStyles.headerContainer, flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start" }}>
          <View style={{ flex: 1 }}>
            <Text style={mergedStyles.name}>
              {personalInfo.firstName} {personalInfo.lastName}
            </Text>
            {(professionalTitle || (workExperience.length > 0 && workExperience[0].jobTitle)) && (
              <Text style={mergedStyles.professionalTitle}>
                {professionalTitle || workExperience[0].jobTitle}
              </Text>
            )}
            <View style={mergedStyles.contactRow}>
              {[
                personalInfo.location,
                personalInfo.email,
                personalInfo.phone && formatPhoneNumber(personalInfo.phone),
              ]
                .filter(Boolean)
                .map((item, index, arr) => (
                  <React.Fragment key={index}>
                    <Text style={index === 1 ? mergedStyles.contactLink : mergedStyles.contactText}>
                      {item}
                    </Text>
                    {index < arr.length - 1 && (
                      <Text style={mergedStyles.contactSeparator}>|</Text>
                    )}
                  </React.Fragment>
                ))}
            </View>
          </View>
          {profilePhotoUrl && (
            <View style={mergedStyles.profilePhotoContainer}>
              <Image src={profilePhotoUrl} style={mergedStyles.profilePhoto} />
            </View>
          )}
        </View>

        {/* Render sections in order */}
        {finalSectionOrder.map((sectionId, index) => {
          const SectionComponent = sectionComponents[sectionId];
          const content = SectionComponent ? SectionComponent() : null;
          return content ? <View key={`${sectionId}-${index}`} wrap={true}>{content}</View> : null;
        })}
      </Page>
    </Document>
  );
};

export default ATSPrimeTemplate;
